// src/pages/Manifeste.js
import React from "react";
import Navigation from "../components/Navigation";
import { useI18n } from "../i18n/LanguageProvider";
import "../styles/settings.scss";
import "../styles/manifeste.scss";

const Manifeste = () => {
  const { t, lang } = useI18n();

  const isFrench = lang === "fr";

  const ledeRaw = t("manifesto.lede");
  const lede =
    typeof ledeRaw === "string" && ledeRaw !== "manifesto.lede" ? ledeRaw : "";

  const bodyMaybe = t("manifesto.bodyHtml");
  const bodyHtml = Array.isArray(bodyMaybe) ? bodyMaybe : [];

  const renderBanner = () => (
    <>
      <hr className="divider-stars" aria-hidden="true" />
      <figure
        className="manifeste-banner"
        role="group"
        aria-label={t("manifesto.bannerAria") || "Illustration de fin du manifeste"}
      >
        <img
          src="/images/banque-photo/img-42.jpg"
          alt={t("manifesto.bannerAlt") || "Illustration"}
          loading="lazy"
        />
      </figure>
    </>
  );

  return (
    <>
      <Navigation />

      <main className="manifeste-page content" role="main">
        <div className="manifeste-text">
          {bodyHtml.length ? (
            <>
              <h1 className="lede-title accent-red">
                {lede ||
                  (isFrench ? (
                    <>
                      Ci-bas se trouve l'introduction du texte{" "}
                      <em>Corne de brume - Intérêt&nbsp;national et gens du commun</em>.{" "}
                      Pour consulter le texte dans son intégralité{" "}
                      <a
                        href="/texts/interetnational.pdf"
                        target="_blank"
                        rel="noreferrer"
                      >
                        cliquez ici
                      </a>
                    </>
                  ) : (
                    "Translation to come"
                  ))}
              </h1>

              {bodyHtml.map((html, i) => (
                <div key={i} dangerouslySetInnerHTML={{ __html: html }} />
              ))}

              {renderBanner()}
            </>
          ) : isFrench ? (
            <>
              <h1 className="lede-title accent-red">
                Ci-bas se trouve l'introduction du texte{" "}
                <em>Corne de brume - les gens du&nbsp;commun&nbsp;contre&nbsp;l'intérêt&nbsp;national</em>.{" "}
                Pour consulter le texte dans son intégralité{" "}
                <a
                  href="/texts/interetnational.pdf"
                  target="_blank"
                  rel="noreferrer"
                >
                  cliquez ici
                </a>
              </h1>

              <p>
                Qu'est-ce qui se cache derrière cette idée d'« intérêt national » ?
                Lancée à tout vent par les politiciens du monde entier, la formule
                semble dissimuler quelque chose de magique, quelque chose
                d'indébattable. Au fond, peut-on vraiment être contre son propre
                intérêt ? Au nom de l’intérêt national, le gouvernement canadien
                accélère la progression du pire: militarisation, infrastructures
                destructrices, surveillance, renforcement frontalier. Au Québec, ce
                sont les grèves, les aides sociales et tout ce qui n'est pas normal
                et productif qui sont attaqués, alors que sont consolidées les
                industries liées à l'armement. Pour nous, le projet est clair : mise
                au travail, mobilisation générale, normalisation et appauvrissement.
                Sorti de la bouche des politiciens, qui parlent toujours au{" "}
                <em>nom du Bien</em>, il semble difficile de refuser un tel mot
                d'ordre. Jusqu'ici, en tout cas, personne ne s'y oppose
                explicitement.
              </p>

              <p>
                Nous sommes dans un changement de séquence politique. Les
                commentateurs annonçaient, depuis quelques décennies, le déclin de
                l'État-nation comme forme politique, autant via la place
                grandissante des multinationales ou des organisations
                transnationales, que via un désintéressement généralisé des
                populations - vis-à-vis d'une politique sous l'emprise des
                spécialistes - et la fin des grandes idéologies. Simultanément,
                depuis les années 90, de plus en plus de mouvements sociaux ont pris
                pour objet des dynamiques surpassant l'échelle étatique.
              </p>

              <p>
                Il y a quelques années seulement, une partie de la jeunesse sut
                pousser les gouvernements à se dire écologistes et à multiplier les
                participations aux sommets internationaux. Ayant conscience du
                caractère transfrontalier et transocéanique des phénomènes
                climatiques, la scène imaginaire de la politique était la Terre
                entière, les objectifs se formulant en termes de réchauffement
                planétaire. Puis, avec une quinte de toux masquée, le mouvement
                s'est volatilisé. Comme bien souvent, les mobilisations qui
                voulaient changer le monde ont simplement réussi à donner au pouvoir
                un nouveau fond de légitimité. Aujourd'hui, c'est la question
                nationale qui occupe tant les esprits et les rêves des plus jeunes
                que les discours des États. Elle se mesure en production
                énergétique, industrielle et militaire, révélant l'annexion
                complète de l'enjeu environnemental au plan publicitaire.
              </p>

              <p>
                Dans un contexte d'instabilité politique mondiale, l'État revient à
                l'avant de la scène et voici la menace qu'il fait planer : ceux qui
                contestent ses décisions s'opposent à la nation tout entière, et
                seront punis en conséquence. En mars 2026, aux États-Unis, des
                manifestants qui s'affrontaient aux assauts de ICE au Texas ont été
                déclarés coupables de terrorisme. À l'issue des procès de
                Prairieland, certains font face à des peines d'au moins 10 ans de
                prison. Ils sont notamment accusés d'être membres d'Antifa, un
                groupe imaginaire invoqué pour désigner tout ce qui, à l'intérieur
                de la frontière, s'oppose frontalement à la politique américaine.
              </p>

              <p>
                Au Canada, c'est par l'évocation fréquente de l'idée l'intérêt
                national qu'on voit ce changement d'attitude. Elle a une longue
                histoire, mais elle a été remise de l'avant par Mark Carney depuis
                son arrivée au pouvoir en 2025 et avec l'adoption de la loi C-5,
                « Loi visant à bâtir le Canada ». Cette loi permet d'exempter
                n'importe quel projet d'infrastructure de tous les critères
                environnementaux et toutes consultations publiques qui auraient le
                potentiel de ralentir ou de bloquer sa réalisation. Comme on le
                sait, même si ces mécanismes peuvent faire gagner du temps à ceux
                qui s'opposent à tel ou tel projet, c'est seulement la volatilité
                des investissements ou la menace d'un combat réel qui peut les
                empêcher. Il faut donc comprendre la dénomination d'intérêt national
                non seulement comme un accélérateur juridique, mais aussi comme une
                préparation idéologique à la répression qui viendra frapper toute
                forme d'opposition. Depuis, on voit régulièrement apparaître le
                terme quand il est question de la reconstruction d'une armée qui
                aurait été négligée dans les années récentes.
              </p>

              <p>
                Le spectre qui hante la loi C-5 et toute la rhétorique de l'intérêt
                national, c'est bien celui du mouvement anti-oléoduc, et en
                particulier l'opposition menée à Coastal Gas Link par les
                Wet'suwet'en sur la côte ouest. C'est le dernier mouvement qui ait
                représenté une menace économique et politique d'un océan à l'autre,
                faisant apparaître la possibilité d'une résurgence autochtone
                militante et un soutien massif à une cause véritablement
                anti-canadienne. À ceux qui ont oublié <em>Shutdown Canada</em>, les
                blocages partout à travers le pays, les sabotages ciblés, les
                actions directes et les manifestations, soyez certains que
                l'appareil d'État, lui, s'en souvient.
              </p>

              <p>
                On peut rappeler les images de policiers ouvrant à la tronçonneuse
                des maisons construites par des opposants au pipeline et arrêtant
                des militantes autochtones. Cette image était brutale, à l'instar
                l'histoire de ce pays. La contre-insurrection canadienne prend
                aussi un tout autre visage depuis ces événements : il s'agit
                d'arranger des compromis entre les entreprises et des politiciens
                autochtones en espérant acheter la paix. Les accords d'
                <em>equity ownership</em>, passés avec les compagnies pétrolières
                afin que les communautés deviennent actionnaires de projets de
                développement, sont une tentative de les intégrer dans le giron de
                l'intérêt national. Si cette opération réussit, l'extraction des
                ressources peut continuer sans crainte. C'est la concession des
                compagnies extractivistes face à la menace d'une opposition
                constante.
              </p>

              <p>
                Dans la continuité du projet colonial canadien, il y a en effet un
                double mouvement: canadianiser les populations autochtones et
                autochtoniser le Canada. Cela suppose de produire l'assentiment des
                instances officielles de la politique autochtone au projet national
                et d'intégrer sa symbolique à l'imaginaire national. L'objectif est
                bien d'éteindre symboliquement l'extériorité des formes autochtones,
                mais aussi de donner une profondeur historique, identitaire et
                territoriale au Canada, à lui assurer des assises solides. Le
                colonialisme ne passe pas uniquement par la subordination des
                colonisés, mais également par l'effort constant de créer un
                sentiment d'appartenance au territoire sous le signe de la nation
                unifiée - unifiée en principe, mais traversée en pratique par une
                séparation raciale bien claire. La poursuite du projet colonial se
                déploie dans l'affirmation d'une volonté nationale de peupler le
                territoire - de manière physique, militaire et esthétique - et
                d'intégrer ce qui s'y trouve déjà à la représentation nationale.
              </p>

              <p>
                Les tarifs mis en place par Trump et ses allusions au fait que le
                Canada devrait être un État américain ont ravivé en retour les
                affirmations, jamais aussi fortes depuis le deuxième référendum sur
                l'indépendance du Québec, d'une identité canadienne et d'une
                identité québécoise. Malgré cette dissonance, il y a consensus: le
                prédateur américain a des visées sur le pays, et il faut le
                défendre. Jusqu'à récemment, la compréhension implicite tenait le
                Canada pour un arrangement économique profitable, les Canadiens des{" "}
                <em>business partners</em>, et la démocratie parlementaire comme le
                processus par lequel le butin peut être divisé entre les citoyens.
                C'est ce qu'on peut lire depuis longtemps sur le site{" "}
                <em>Mohawk Nation News</em> : <em>« Canada the Corporation »</em>,
                Canada l'entreprise. La Confédération, dans son fondement, est
                l'officialisation politique d'une entente dont l'objectif est de
                consolider les investissements d'industriels entre les différentes
                provinces britanniques. Elle vise à « protéger les intérêts des
                minorités, et les riches ne sont-ils pas toujours moins nombreux
                que les pauvres? », dixit John A. Macdonald. La logique était
                qu'une fois imposée par la force, cette union commerciale finirait
                bien par être acceptée. La construction nationale, l'idée d'un
                intérêt national comme orientation politique, est l'héritage de
                cette imposition étatique.
              </p>

              <p>
                Ce concept d'intérêt national, quoi que flou en apparence, n'est
                donc pas complètement vide : il existe bien une prospérité, une
                sécurité, des frontières canadiennes qui peuvent « se défendre ».
                Nous nous opposons à l'intérêt national canadien non pas parce qu'il
                est creux, mais plutôt parce que nous savons ce qu'il implique ici
                et ailleurs. L'intérêt national, c'est l'affirmation d'un projet
                politique partagé par l'intégralité des positions parlementaire, de
                droite comme de gauche : paix sociale, démocratie, prospérité,
                protection des valeurs et de la culture. Tels sont les mots dont la
                situation actuelle se drape pour cacher son corps monstrueux. Contre
                cette volonté de l'État de se défendre comme une forteresse, nous
                devons répondre qu'<em>il n'y a qu'un seul monde</em>. C'est bien là
                le constat de l'hégémonie du capitalisme - le fait que la richesse
                canadienne, de même que celle de tous les pays riches, est issue de
                l'intégration progressive de l'ensemble de la planète à son système
                d'extraction et de destruction. En cela, la promesse du nationalisme
                progressiste, qui vise à faire appartenir tout un chacun à la
                communauté nationale, est la continuation du programme impérial du
                Canada. Dire qu'il n'y a qu'un monde, c'est aussi formuler la
                possibilité d'un nouvel internationalisme, d'une politique qui
                assume que, de toute part, nous sommes de partout et des attachements
                nous tiennent au-delà des frontières.
              </p>

              <p>
                Projeter la politique sur la base d'une appartenance à la nation,
                c'est toujours entrer dans la logique de l'intérêt national, et
                jamais se pencher sur ce qu'on porte vraiment politiquement. Tout le
                commentariat autour d'une possible invasion américaine du Canada
                masque le fait que si cela advenait, ça ne changerait à peu près
                rien. On ne nous prendra pas au jeu de la défense de la nation. En
                regard des questions nationales, le seul programme révolutionnaire,
                c'est celui-ci : rompre la nation. De la même manière, le relent
                souverainiste dissimule le fait que l'indépendance du Québec
                changerait à peu près rien : distinct (comme les autres) il pourrait
                devenir un pays (comme les autres). «L'indépendance avant tout»
                masque l'absence d'avenues politiques désirables. Mais en quoi
                l'accession à la souveraineté nous prémunirait-elle de tout ce qui
                affecte les autres pays souverains : homogénéisation culturelle,
                militarisation, intégration au système prédateur de l'économie-monde?
                Ce qui fait l'objet de ce fantasme de pays, c'est le désir d'une
                communauté qui se représenterait enfin comme ayant force de loi.
                Est-ce un hasard si les Québécois ont commencé à se prendre pour une
                nation en signant leur traité frauduleux avec les Cris et les
                Inuits? Il y a quelque chose de l'ordre du cannibalisme dans la
                formation de toute nation.
              </p>

              <p>
                Dans les pages qui suivent, nous décortiquerons ce qui se cache sous
                cette rhétorique de l'intérêt national : un outil pour gouverner,
                une autre opération pour justifier la guerre, l'exploitation, la
                surveillance, la mise au travail. Contre la logique qu'elle
                sous-tend, nous proposons une autre appartenance, une autre manière
                de se nommer et de se penser. Gens du commun contre l'intérêt
                national, c'est par cette formule que nous proposons une nouvelle
                division politique.
              </p>

              {renderBanner()}
            </>
          ) : (
            <>
              <h1 className="lede-title accent-red">Translation to come</h1>
              {renderBanner()}
            </>
          )}
        </div>
      </main>
    </>
  );
};

export default Manifeste;